import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';

export type RowStatus =
  | 'Success'
  | 'WouldUpdate'
  | 'Skipped'
  | 'Mismatch'
  | 'EmployeeNotFound'
  | 'TimesheetNotFound'
  | 'NoAdpId'
  | 'InvalidDrawCarryover'
  | 'Error';

export interface DeficitRow {
  Row: number;
  LastName: string;
  FirstName: string;
  AdpId: string;
  DrawCarryoverText: string;
  DrawCarryover: number | null;
  PreviousDrawCarryover: number | null;
  Status: RowStatus;
  Message: string;
}

export interface DeficitResponse {
  Success: boolean;
  DryRun: boolean;
  ErrorMessage: string | null;
  PayrollEndDate: string | null;
  PayDate: string | null;
  PeriodId: number | null;
  Rows: DeficitRow[];
  TotalRows: number;
  UpdatedCount: number;
  SkippedCount: number;
  ErrorCount: number;
  MismatchCount: number;
}

export interface PeriodOption {
  PeriodId: number;
  LowerDate: string;
  UpperDate: string;
}

export interface EmployeeSearchCandidate {
  EmployeeId: number;
  FirstName: string;
  LastName: string;
  PayrollExternalID: string | null;
  IsActive: boolean;
  HasTimesheetThisPeriod: boolean;
}

export interface ManageDeficitRow {
  TimesheetId: number;
  LastName: string;
  FirstName: string;
  AdpId: string;
  DrawCarryover: number;
}

export interface ManageDeficitsReportResponse {
  Rows: ManageDeficitRow[];
  TotalDrawCarryover?: number;
  DeficitsProcessedByName?: string | null;
  DeficitsProcessedByEmail?: string | null;
}

export interface ManualProcessRow {
  Row: number;
  AdpId: string;
  DrawCarryoverText: string;
  MismatchResolution?: 'entered' | 'database';
}

export function useDeficitPeriods() {
  return useQuery({
    queryKey: ['deficits', 'periods'],
    queryFn: async () => {
      const { data } = await api.get<PeriodOption[]>('/deficits/periods');
      return data;
    },
  });
}

export function useManagedDeficits(periodId: number | null, enabled: boolean) {
  return useQuery({
    queryKey: ['deficits', 'manage', periodId],
    queryFn: async () => {
      const { data } = await api.get<ManageDeficitsReportResponse>(
        `/deficits/manual/manage/${periodId}`
      );
      return data;
    },
    enabled: enabled && periodId != null && periodId > 0,
  });
}

export function useManualProcessDeficits() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      periodId,
      rows,
      dryRun,
    }: {
      periodId: number;
      rows: ManualProcessRow[];
      dryRun: boolean;
    }) => {
      const { data } = await api.post<DeficitResponse>(
        `/deficits/manual/process?dryRun=${dryRun}`,
        { PeriodId: periodId, Rows: rows }
      );
      return data;
    },
    onSuccess: (_data, variables) => {
      if (!variables.dryRun) {
        queryClient.invalidateQueries({ queryKey: ['deficits', 'manage'] });
      }
    },
  });
}

export function useSearchEmployees() {
  return useMutation({
    mutationFn: async ({
      periodId,
      firstName,
      lastName,
    }: {
      periodId: number;
      firstName?: string;
      lastName?: string;
    }) => {
      const params = new URLSearchParams({ periodId: String(periodId) });
      if (firstName?.trim()) params.set('firstName', firstName.trim());
      if (lastName?.trim()) params.set('lastName', lastName.trim());
      const { data } = await api.get<EmployeeSearchCandidate[]>(
        `/deficits/employees/search?${params}`
      );
      return data;
    },
  });
}

export function useAssignPayrollExternalId() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      employeeId,
      payrollExternalId,
    }: {
      employeeId: number;
      payrollExternalId: string;
    }) => {
      await api.post('/deficits/employees/assign-payroll-external-id', {
        EmployeeId: employeeId,
        PayrollExternalId: payrollExternalId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deficits'] });
    },
  });
}
